import type { IHttpResponse } from '@/store/definition';

// デフォルトタイムアウト秒数（ミリ秒）
const DEFAULT_TIMEOUT = 30000;

interface IApiWrapper {
  get(
    uri: string,
    headersParam: any,
    queryParam: any,
    maxRetryCount: any,
    errorCallback: any
  ): Promise<IHttpResponse>;
  post(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: any,
    errorCallback: any
  ): Promise<IHttpResponse>;
  postConnectionTimeoutNotRetry(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: any,
    errorCallback: any
  ): Promise<IHttpResponse>;
  put(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: any,
    errorCallback: any
  ): Promise<IHttpResponse>;
  putConnectionTimeoutNotRetry(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: any,
    errorCallback: any
  ): Promise<IHttpResponse>;
  delete(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: any,
    errorCallback: any
  ): Promise<IHttpResponse>;
}

class ApiWrapper implements IApiWrapper {
  /**
   * GETメソッドを実行する.
   *
   * @param {string} uri          URI
   * @param {any} headersParam    ヘッダパラメータ
   * @param {any} queryParam      クエリパラメータ
   * @param {any} maxRetryCount   最大リトライ回数
   * @param {any} errorCallback   エラーコールバック関数
   * @returns {IHttpResponse}      HTTPレスポンス
   */
  async get(
    uri: string,
    headersParam: any,
    queryParam: any,
    maxRetryCount: any,
    errorCallback: any,
    timeout: number = DEFAULT_TIMEOUT
  ): Promise<IHttpResponse> {
    const { $httpClient } = useNuxtApp();
    const requestTimeoutRetry = true;
    const errorCodeRequestTimeoutRetry = true;

    const response = await $httpClient.getData(
      uri,
      headersParam,
      queryParam,
      maxRetryCount,
      requestTimeoutRetry,
      errorCodeRequestTimeoutRetry,
      errorCallback,
      timeout,
      crypto.randomUUID()
    );

    return response;
  }

  /**
   * POSTメソッドを実行する.
   *
   * @param {string} uri          URI
   * @param {any} headersParam    ヘッダパラメータ
   * @param {any} queryParam      クエリパラメータ
   * @param {any} body            ボディパラメータ
   * @param {any} maxRetryCount   最大リトライ回数
   * @param {any} errorCallback   エラーコールバック関数
   * @returns {IHttpResponse}      HTTPレスポンス
   */
  async post(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: any,
    errorCallback: any,
    timeout: number = DEFAULT_TIMEOUT
  ): Promise<IHttpResponse> {
    const { $httpClient } = useNuxtApp();
    const requestTimeoutRetry = true;
    const errorCodeRequestTimeoutRetry = true;

    const response = await $httpClient.postData(
      uri,
      headersParam,
      queryParam,
      body,
      maxRetryCount,
      requestTimeoutRetry,
      errorCodeRequestTimeoutRetry,
      errorCallback,
      timeout
    );

    return response;
  }

  /**
   * POSTメソッドを実行する(接続タイムアウトはリトライなし).
   *
   * @param {string} uri          URI
   * @param {any} headersParam    ヘッダパラメータ
   * @param {any} queryParam      クエリパラメータ
   * @param {any} body            ボディパラメータ
   * @param {any} maxRetryCount   最大リトライ回数
   * @param {any} errorCallback   エラーコールバック関数
   * @returns {IHttpResponse}      HTTPレスポンス
   */
  async postConnectionTimeoutNotRetry(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: any,
    errorCallback: any,
    timeout: number = DEFAULT_TIMEOUT
  ): Promise<IHttpResponse> {
    const { $httpClient } = useNuxtApp();
    const requestTimeoutRetry = false;
    const errorCodeRequestTimeoutRetry = true;

    const response = await $httpClient.postData(
      uri,
      headersParam,
      queryParam,
      body,
      maxRetryCount,
      requestTimeoutRetry,
      errorCodeRequestTimeoutRetry,
      errorCallback,
      timeout
    );

    return response;
  }

  /**
   * PUTメソッドを実行する.
   *
   * @param {string} uri          URI
   * @param {any} headersParam    ヘッダパラメータ
   * @param {any} queryParam      クエリパラメータ
   * @param {any} body            ボディパラメータ
   * @param {any} maxRetryCount   最大リトライ回数
   * @param {any} errorCallback   エラーコールバック関数
   * @returns {IHttpResponse}      HTTPレスポンス
   */
  async put(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: any,
    errorCallback: any,
    timeout: number = DEFAULT_TIMEOUT
  ): Promise<IHttpResponse> {
    const { $httpClient } = useNuxtApp();
    const requestTimeoutRetry = true;
    const errorCodeRequestTimeoutRetry = true;

    const response = await $httpClient.putData(
      uri,
      headersParam,
      queryParam,
      body,
      maxRetryCount,
      requestTimeoutRetry,
      errorCodeRequestTimeoutRetry,
      errorCallback,
      timeout
    );

    return response;
  }

  /**
   * PUTメソッドを実行する(接続タイムアウトはリトライなし).
   *
   * @param {string} uri          URI
   * @param {any} headersParam    ヘッダパラメータ
   * @param {any} queryParam      クエリパラメータ
   * @param {any} body            ボディパラメータ
   * @param {any} maxRetryCount   最大リトライ回数
   * @param {any} errorCallback   エラーコールバック関数
   * @returns {IHttpResponse}      HTTPレスポンス
   */
  async putConnectionTimeoutNotRetry(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: any,
    errorCallback: any,
    timeout: number = DEFAULT_TIMEOUT
  ): Promise<IHttpResponse> {
    const { $httpClient } = useNuxtApp();
    const requestTimeoutRetry = false;
    const errorCodeRequestTimeoutRetry = true;

    const response = await $httpClient.putData(
      uri,
      headersParam,
      queryParam,
      body,
      maxRetryCount,
      requestTimeoutRetry,
      errorCodeRequestTimeoutRetry,
      errorCallback,
      timeout
    );

    return response;
  }

  /**
   * DELETEメソッドを実行する.
   *
   * @param {string} uri          URI
   * @param {any} headersParam    ヘッダパラメータ
   * @param {any} queryParam      クエリパラメータ
   * @param {any} body            ボディパラメータ
   * @param {any} maxRetryCount   最大リトライ回数
   * @param {any} errorCallback   エラーコールバック関数
   * @returns {IHttpResponse}      HTTPレスポンス
   */
  async delete(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: any,
    errorCallback: any,
    timeout: number = DEFAULT_TIMEOUT
  ): Promise<IHttpResponse> {
    const { $httpClient } = useNuxtApp();
    const requestTimeoutRetry = true;
    const errorCodeRequestTimeoutRetry = true;

    const response = await $httpClient.deleteData(
      uri,
      headersParam,
      queryParam,
      body,
      maxRetryCount,
      requestTimeoutRetry,
      errorCodeRequestTimeoutRetry,
      errorCallback,
      timeout
    );

    return response;
  }
}

export default defineNuxtPlugin(() => {
  return {
    provide: {
      DEFAULT_TIMEOUT: DEFAULT_TIMEOUT,
      ApiWrapper: new ApiWrapper(),
    },
  };
});
